import { useCallback, useEffect, useRef, useState } from 'react';

import { ToastProps, useToast } from '@rbilabs/universal-components';
import { useIntl } from 'react-intl';

import { useNavigation } from 'hooks/navigation/use-navigation';
import { CustomEventNames, EventTypes, useMParticleContext } from 'state/mParticle';

import { ModalAuthScreen, ModalAuthState, ModalAuthTrigger } from '../types';

export interface OpenSignInModalProps {
  errorMessage?: string;
  shouldWaitLoginForVendor?: boolean;
  shouldDisableHeadlineLinks?: boolean;
  navigateOnSuccessRoute?: string;
  trigger?: ModalAuthState['trigger'];
}

interface OnSignInSuccessProps {
  navigateOnSuccessRoute?: string;
}

export interface ModalAuthHook {
  modalAuthState: ModalAuthState;
  setModalAuthState: (modalAuthstate: ModalAuthState) => void;
  modalAuthIsOpen: boolean;
  openSignInModal: (params?: OpenSignInModalProps) => void;
  onSignInSuccess: (params?: OnSignInSuccessProps) => void;
  closeModalAuth: () => void;
}

// Initial state for the modal authentication hook.
// This defines the default values for all modal properties when the component mounts or when the modal is explicitly closed.
const initialModalAuthState: ModalAuthState = {
  screen: ModalAuthScreen.CLOSED,
  shouldDisplaySignInSuccessMessage: true,
  shouldDisableHeadlineLinks: false,
  shouldWaitLoginForVendor: false,
  disableEmailInput: false,
  loginForVendorIsLoading: false,
  trigger: ModalAuthTrigger.Global,
  onClose: undefined,
  onPreSignIn: undefined,
  onSignInSuccess: undefined,
  user: undefined,
  providerType: undefined,
  errorMessage: '',
  heading: '',
};

// This hook is used to track the modal auth state. Which is used to determine if the user is in the modal auth state or not.
export const useModalAuth = (): ModalAuthHook => {
  const { logRBIEvent } = useMParticleContext();
  const toast = useToast();
  const { formatMessage } = useIntl();
  const { navigate } = useNavigation();

  const [modalAuthState, setModalAuthState] = useState<ModalAuthState>(initialModalAuthState);

  const modalAuthStateRef = useRef(modalAuthState);

  useEffect(() => {
    modalAuthStateRef.current = modalAuthState;
  }, [modalAuthState]);

  const modalAuthIsOpen = modalAuthState.screen !== ModalAuthScreen.CLOSED;

  /**
   * A handler function to update the modal authentication state.
   * It merges the new state with the previous state, ensuring that no data is lost
   * and only specified properties are updated.
   *
   * @param {Partial<ModalAuthState>} newState - The new state to be merged with the previous state.
   */

  const handleSetModalAuthState = useCallback(
    (newState: Partial<ModalAuthState>) => {
      setModalAuthState(prevModalAuthState => {
        const mergedState = {} as ModalAuthState;
        // Merge keys from both prevModalAuthState and newState
        const allKeys = new Set([...Object.keys(prevModalAuthState), ...Object.keys(newState)]);

        // This approach enables dynamic addition of new states created in the modalAuthState from outside this code,
        // helping to avoid issues if we forget to include them explicitly.
        allKeys.forEach(key => {
          // Special handling for 'user' object
          if (key === 'user') {
            const prevUser = prevModalAuthState.user || {};
            const newUser = newState.user || {};
            mergedState.user = {
              name: newUser.name ?? prevUser.name,
              email: newUser.email ?? prevUser.email,
            };
          } else {
            mergedState[key] = newState[key] ?? prevModalAuthState[key];
          }
        });

        if (newState.screen && prevModalAuthState.screen !== newState.screen) {
          logRBIEvent({
            name: CustomEventNames.AUTH_DRAWER_VIEW,
            type: EventTypes.Other,
            attributes: {
              screen: newState.screen,
            },
          });
        }

        return mergedState;
      });
    },
    [logRBIEvent]
  );

  const handleCloseModalAuth = useCallback(() => {
    return setModalAuthState(initialModalAuthState);
  }, []);

  const onSignInSuccess = useCallback(
    (params?: OnSignInSuccessProps) => {
      const { navigateOnSuccessRoute } = params || {};
      const shouldShowToast = !!modalAuthStateRef.current.shouldDisplaySignInSuccessMessage;

      const successToast: ToastProps = {
        text: formatMessage({ id: 'signInSuccess' }),
        variant: 'positive',
      };

      if (!navigateOnSuccessRoute && shouldShowToast) {
        toast.show(successToast);
      }

      if (navigateOnSuccessRoute?.length) {
        navigate(navigateOnSuccessRoute, {
          state: {
            toast: shouldShowToast ? successToast : '',
          },
        });
      }
      handleCloseModalAuth();
    },
    [handleCloseModalAuth, formatMessage, navigate, toast]
  );

  const openSignInModal = useCallback(
    (params?: OpenSignInModalProps) => {
      const {
        errorMessage = '',
        shouldDisableHeadlineLinks = false,
        shouldWaitLoginForVendor = false,
        navigateOnSuccessRoute,
        trigger = ModalAuthTrigger.Global,
      } = params || {};

      handleSetModalAuthState({
        screen: ModalAuthScreen.SIGN_IN,
        onSignInSuccess: () => onSignInSuccess({ navigateOnSuccessRoute }),
        errorMessage,
        shouldDisableHeadlineLinks,
        shouldWaitLoginForVendor,
        trigger,
      });
    },
    [handleSetModalAuthState, onSignInSuccess]
  );

  return {
    modalAuthState,
    setModalAuthState: handleSetModalAuthState,
    modalAuthIsOpen,
    openSignInModal,
    onSignInSuccess,
    closeModalAuth: handleCloseModalAuth,
  };
};
